import { Injectable, OnDestroy } from '@angular/core';
import { Table } from '@model/reservation';
import { BatchAffectedSection, BatchAffectedTable, Section, WorkingDate, WorkingDateType } from '@model/tables-plan';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { State } from '@store/index';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import moment from 'moment';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { TablesPlanApiService } from 'src/app/apis/tables-plan-api.service';
import { rect10_Dimension, rect2_Dimension, rect4_Dimension, rect6_Dimension, rect8_Dimension, round10_Dimension, round2_Dimension, round4_Dimension, round6_Dimension, round8_Dimension, seabed_Dimension, square2_Dimension, square4_Dimension, tableImageSizeFactor } from '../constants';
import { Common } from '../lib/common-methods';

const coordinatesSizeFactor = 0.001;

@Injectable({
  providedIn: 'root'
})
export class TablesPlanService implements OnDestroy {

  constructor(
    private translateService: TranslateService,
    private tablesPlanApiService: TablesPlanApiService,
    private store: Store<State>,
  ) {

    this.tables$.pipe(takeUntil(this.destroy$)).subscribe((tables) => {
      this.tables = tables;
    });

    this.sections$.pipe(takeUntil(this.destroy$)).subscribe((sections) => {
      this.sections = sections;
    });

  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  tables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectTables));
  tables: Table[] = [];
  sections$: Observable<Section[] | null> = this.store.pipe(select(TablesPlanSelectors.selectSections));
  sections: Section[] | null = null;

  coordinateToPixels(coordinate: number) {
    return Math.round(coordinate * coordinatesSizeFactor);
  }

  pixelsToCoordinate(pixels: number) {
    return Math.round(pixels / coordinatesSizeFactor);
  }

  static createSpecialFromRegularWorkingDate(workingDate: WorkingDate, sections: Section[], tables: Table[], date: Date) {

    const copy = TablesPlanService.copyWorkingDate(workingDate, sections, tables, date);

    copy.workingDate.isCustom = true;
    copy.workingDate.typeOfWorkingDate = 'special' as WorkingDateType;

    return copy;
  }

  static createRegularFromSpecialWorkingDate(workingDate: WorkingDate, sections: Section[], tables: Table[], date: Date) {

    const copy = TablesPlanService.copyWorkingDate(workingDate, sections, tables, date);

    copy.workingDate.isCustom = false;
    copy.workingDate.typeOfWorkingDate = 'regular' as WorkingDateType;
    copy.workingDate.workingDate = '1970-01-01';

    return copy;
  }

  static createNewSpecialWorkingDate(restaurantId: string, date: Date) {

    const workingDate: WorkingDate = {
      workingDateId: Common.generateUuidBase64Encoded(),
      restaurantId,
      workingDate: moment(date).format('yyyy-MM-DD'),
      typeOfWorkingDate: 'special' as WorkingDateType,
      version: 0,
      isCustom: true,
      timeperformed: (new Date()).getTime()
    };

    return workingDate;
  }

  // copy WorkingDate, Sections and Tables (assign the newly created workingDateId and sectionIds)
  static copyWorkingDate(workingDate: WorkingDate, sections: Section[], tables: Table[], date: Date) {
    const workingDateId = Common.generateUuidBase64Encoded();

    const oldToNewsectionIdsDictionary: { [oldId: string]: string; } = {};
    for (let section of sections) {
      oldToNewsectionIdsDictionary[section.sectionId] = Common.generateUuidBase64Encoded();
    }

    return {
      workingDate: {
        ...workingDate,
        workingDateId,
        workingDate: moment(date).format('yyyy-MM-DD'),
        timeperformed: (new Date()).getTime(),
      },
      sections: sections.map(s => {
        return { ...s, workingDateId, sectionId: oldToNewsectionIdsDictionary[s.sectionId] }
      }),
      tables: tables.map(t => {
        const tableId = Common.generateUuidBase64Encoded();
        return { ...t, workingDateId, sectionId: oldToNewsectionIdsDictionary[t.sectionId], tableId }
      }),
      oldToNewsectionIdsDictionary
    };
  }

  // if a Table's occurances (by tableId) is more than one keep the latest one (by pandingSyncOrder)
  static reduceAffectedTables(affectedTables: BatchAffectedTable[]): BatchAffectedTable[] {

    const reducedTables: BatchAffectedTable[] = [];

    // order by pendingSyncOrder
    affectedTables = affectedTables.sort(TablesPlanService.pendingSyncOrderComparator);

    for (let affectedTable of affectedTables) {

      const existingTable = reducedTables.find(t => t.tableDetails.tableId === affectedTable.tableDetails.tableId);

      if (existingTable == null) {
        reducedTables.push(affectedTable);
        continue;
      }

      const index = reducedTables.indexOf(existingTable);
      reducedTables[index] = affectedTable;
    }

    return reducedTables;
  }

  // if a Section's occurances (by tablsectionId) is more than one keep the latest one (by pandingSyncOrder)
  static reduceAffectedSections(affectedSections: BatchAffectedSection[]): BatchAffectedSection[] {

    const reducedSections: BatchAffectedSection[] = [];

    // order by pendingSyncOrder
    affectedSections = affectedSections.sort(TablesPlanService.pendingSyncOrderComparator);

    for (let affectedSection of affectedSections) {

      const existingSection = reducedSections.find(s => s.sectionDetails.sectionId === affectedSection.sectionDetails.sectionId);

      if (existingSection == null) {
        reducedSections.push(affectedSection);
        continue;
      }

      const index = reducedSections.indexOf(existingSection);
      reducedSections[index] = affectedSection;
    }

    return reducedSections;
  }

  private static pendingSyncOrderComparator(a: any, b: any): number {
    if (a.pendingSyncOrder > b.pendingSyncOrder) {
      return 1;
    }
    if (a.pendingSyncOrder < b.pendingSyncOrder) {
      return -1;
    }
    return 0;
  }

  public static sectionOrderComparator(a: Section, b: Section): number {
    if (a.sectionOrder > b.sectionOrder) {
      return 1;
    }
    if (a.sectionOrder < b.sectionOrder) {
      return -1;
    }
    return 0;
  }

  isTableValid(table: Table): string | null {
    // check empty name
    if (Common.isNullOrEmpty(table.tableName)) {
      return this.translateService.instant('tablesPlan.editMode.editTableEmptyName');
    }

    // check invalid name
    if (!this.isNameValid(table?.tableName as string)) {
      return this.translateService.instant('tablesPlan.editMode.editTableInvalidName');
    }

    // check empty order
    if (Common.isNullOrEmpty((table?.tableOrder ?? '').toString())) {
      return this.translateService.instant('tablesPlan.editMode.editTableEmptyOrder');
    }

    // check invalid order
    if (!this.isOrderValid(table?.tableOrder)) {
      return this.translateService.instant('tablesPlan.editMode.editTableInvalidOrder');
    }

    // check empty min capacity
    if (table?.minCapacity == null || table?.minCapacity <= 0) {
      return this.translateService.instant('tablesPlan.editMode.editTableNoMin');
    }

    // check empty max capacity
    if (table?.maxCapacity == null || table?.maxCapacity <= 0) {
      return this.translateService.instant('tablesPlan.editMode.editTableNoMax');
    }

    // check min capacity > max capacity
    if (table?.minCapacity > table?.maxCapacity) {
      return this.translateService.instant('tablesPlan.editMode.editTableMinMoreThanMax');
    }

    // check table name is unique
    if (this.tables.filter(t => t.tableId !== table.tableId).some(t => t.tableName === table.tableName)) {
      return this.translateService.instant('tablesPlan.editMode.tableAlreadyExists');
    }

    return null;
  }

  isNameValid(name: string | null): boolean {
    if (Common.isNullOrEmpty(name)) {
      return false;
    }
    return (name as string).match(/[*+%!$&,:;=?@#._/|]/gm) == null;
  }

  isOrderValid(order: number | string | undefined): boolean {
    if (order == null || Common.isNullOrEmpty(order.toString())) {
      return false;
    }

    if (order.toString().match(/[*+%!$&,:;=?@#._/|]/gm) != null) {
      return false;
    }

    const num = parseInt(order.toString());

    return !isNaN(num) && num > 0;
  }

  isSectionValid(section: Section): string | null {

    if (section == null) {
      return null;
    }

    // check empty name
    if (Common.isNullOrEmpty(section.sectionName)) {
      return this.translateService.instant('tablesPlan.editMode.editSectionEmptyName');
    }

    // check invalid name
    if (!this.isNameValid(section?.sectionName as string)) {
      return this.translateService.instant('tablesPlan.editMode.editSectionInvalidName');
    }

    // check empty order
    if (Common.isNullOrEmpty((section?.sectionOrder ?? '').toString())) {
      return this.translateService.instant('tablesPlan.editMode.editSectionEmptyOrder');
    }

    // check invalid order
    if (!this.isOrderValid(section?.sectionOrder)) {
      return this.translateService.instant('tablesPlan.editMode.editSectionInvalidOrder');
    }

    // check section name is unique
    if ((this.sections ?? []).filter(s => s.sectionId !== section.sectionId).some(s => s.sectionName === section.sectionName)) {
      return this.translateService.instant('tablesPlan.editMode.sectionAlreadyExists');
    }

    return null;
  }

  tableCanBeJoined(table: Table, joinedTables: Table[]): boolean {

    const tables = joinedTables.concat(table);

    const onlineCategories = [...new Set(tables.map(t => {
      return t.onlineCategory?.id != null ? t.onlineCategory.id : '';
    }))];
    if (onlineCategories.length > 1) {
      return false;
    }

    const excludeOwnChannels = [...new Set(tables.map(t => t.excludeOnline))].filter(t => t != null);
    if (excludeOwnChannels.length > 1) {
      return false;
    }

    const excludeThirdPartyChannels = [...new Set(tables.map(t => t.excludeOnlineThirdParty))].filter(t => t != null);
    if (excludeThirdPartyChannels.length > 1) {
      return false;
    }

    return true;
  }

  getJoinedParticipantTables(joinedTables: Table[], tables: Table[]): Table[] {

    if (joinedTables == null || joinedTables.length <= 0 || tables == null || tables.length <= 0) {
      return [];
    }

    const tableIds = [...new Set(joinedTables.map(t => t.joinTablesIds).join(';').split(';'))];
    return tables.filter(t => tableIds.includes(t.tableId));
  }

  static getJoinedTableMinCapacity(tables: Table[]) {
    const minCapacities = tables.map(t => t.minCapacity);
    const maxCapacities = tables.map(t => t.maxCapacity);

    const maxTableCapacity = Math.max(...maxCapacities);
    const tableWithMaxCapacity = tables.find(t => t.maxCapacity == maxTableCapacity) as Table;

    return minCapacities.reduce((a, b) => a + b, 0)
      - tableWithMaxCapacity?.minCapacity
      + tableWithMaxCapacity?.maxCapacity;
  }

  getRegularTablesPlan(restaurantId: number | string): Observable<WorkingDate> {
    const tenYearsFromNow = new Date();
    tenYearsFromNow.setFullYear(tenYearsFromNow.getFullYear() + 10);

    return this.tablesPlanApiService.getTablesPlan(restaurantId, tenYearsFromNow).pipe(
      map((res) => {
        return res.workingDate;
      }));
  }

  getTableCoordinatesOffset(imgType: string | undefined) {
    let offset = 0;
    switch (imgType) {
      case 'square2':
        offset = (square2_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'square4':
        offset = (square4_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'rect2':
        offset = (rect2_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'rect4':
        offset = (rect4_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'rect6':
        offset = (rect6_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'rect8':
        offset = (rect8_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'rect10':
        offset = (rect10_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'round2':
        offset = (round2_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'round4':
        offset = (round4_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'round6':
        offset = (round6_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'round8':
        offset = (round8_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'round10':
        offset = (round10_Dimension * tableImageSizeFactor) / 2;
        break;
      case 'seabed':
        offset = (seabed_Dimension * tableImageSizeFactor) / 2;
        break;
      default:
        console.warn(`calculate table position offset, '${imgType}' not found`);
    }

    return offset;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
